/**
 * Asset type definitions
 * Extends OpenAPI generated types with domain-specific types
 */

import {
  AssetResponse,
  DerivedAsset as ApiDerivedAsset,
  LocatorRule,
} from "@/services/api/types.gen";

/**
 * Base asset data type from API response (without derivedAssets and durationMs)
 */
type AssetBase = Omit<
  NonNullable<AssetResponse["data"]>,
  "derivedAssets" | "durationMs"
>;

/**
 * Asset data type extracted from OpenAPI generated AssetResponse
 * All base fields are required for application use
 * derivedAssets and durationMs are optional since not all assets have them
 */
export type Asset = Required<
  Omit<
    AssetBase,
    | "nodeId"
    | "workflowExecutionId"
    | "workflowAssetNumber"
    | "nodeAssetNumber"
    | "terminalAssetNumber"
  >
> &
  Pick<
    AssetBase,
    | "nodeId"
    | "workflowExecutionId"
    | "workflowAssetNumber"
    | "nodeAssetNumber"
    | "terminalAssetNumber"
  > & {
    derivedAssets?: ApiDerivedAsset[];
    thumbnailUrl?: string;
    durationMs?: number | null;
    // Linked audio/video asset fields
    linkedAudioAssetId?: string | null;
    linkedVideoAssetId?: string | null;
    audioExtractionStatus?:
      "pending" | "processing" | "complete" | "failed" | "silent" | null;
  };

export type AssetType = "image" | "video" | "audio" | "document" | "other";

/**
 * Asset dimensions for images and videos
 */
export interface AssetDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

/**
 * Extended metadata extracted from assets client-side
 */
export interface AssetMetadata {
  dimensions?: AssetDimensions;
  duration?: number; // For video/audio in seconds
}

export interface AssetWithMetadata extends Asset {
  type: AssetType;
  displayName: string;
  extension: string;
  formattedSize: string;
}

/**
 * Locator rule (from DB, cached on app load).
 * Derives from OpenAPI-generated LocatorRule with required fields
 * and a richer parameterSpecs shape.
 */
export type AssetLocatorRule = Required<
  Pick<
    LocatorRule,
    "ruleKey" | "displayName" | "pathTemplate" | "mimeType" | "outputMode"
  >
> & {
  parameterSpecs: Record<
    string,
    {
      type: "enum" | "auto";
      values?: string[];
      format?: string;
    }
  >;
};

/**
 * Derived asset group (returned inline from API).
 * Derives from OpenAPI-generated DerivedAsset with required fields
 * and narrowed params type (string values only).
 */
export type DerivedAsset = Required<
  Pick<
    ApiDerivedAsset,
    | "id"
    | "ruleKey"
    | "status"
    | "fileCount"
    | "totalFileSize"
    | "pathTemplate"
    | "parameterSpecs"
    | "outputMode"
    | "mimeType"
  >
> & {
  params: Record<string, string>;
};

/**
 * Augmented asset type that includes derived assets inline.
 * Alias for Asset since derivedAssets is already optional on Asset.
 */
export type AssetWithDerived = Asset;

export const getAssetType = (contentType: string): AssetType => {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";
  if (
    contentType.includes("pdf") ||
    contentType.includes("document") ||
    contentType.includes("text")
  )
    return "document";
  return "other";
};

export const formatFileSize = (bytes: number | undefined | null): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes))
    return "Unknown filesize";
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      } else {
        reject(new Error("Failed to read file as base64"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Format dimensions as "1920x1080"
 */
export const formatDimensions = (dimensions: AssetDimensions): string => {
  return `${dimensions.width}x${dimensions.height}`;
};

/**
 * Format aspect ratio as common name (16:9, 4:3, etc.) or decimal
 */
export const formatAspectRatio = (ratio: number): string => {
  const commonRatios: Record<string, number> = {
    "16:9": 16 / 9,
    "4:3": 4 / 3,
    "1:1": 1,
    "21:9": 21 / 9,
    "9:16": 9 / 16,
    "3:4": 3 / 4,
    "2:1": 2,
    "1:2": 0.5,
  };

  for (const [name, value] of Object.entries(commonRatios)) {
    if (Math.abs(ratio - value) < 0.02) return name;
  }
  return ratio.toFixed(2) + ":1";
};

/**
 * Format dimensions with aspect ratio: "16:9 (1920x1080)"
 */
export const formatDimensionsWithAspect = (
  dimensions: AssetDimensions,
): string => {
  const aspectStr = formatAspectRatio(dimensions.aspectRatio);
  return `${aspectStr} (${formatDimensions(dimensions)})`;
};

/**
 * Format video/audio duration as "MM:SS" or "H:MM:SS"
 */
export const formatDuration = (seconds: number): string => {
  if (
    seconds === undefined ||
    seconds === null ||
    isNaN(seconds) ||
    !isFinite(seconds)
  ) {
    return "";
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
};
